import React, { useState, useEffect, useRef } from "react";
import axiosInstance from '../axiosInstance';
import { useMemo } from 'react';
import { Button, Modal, Form, Pagination } from "react-bootstrap";
import { FaSearch, FaSortUp, FaSortDown, FaPen, FaPlus, FaFilter, FaTrash, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { API_URL_All } from "../api";
//import "react-toastify/dist/ReactToastify.css";
import { ClockIcon, MedalGold, MedalSilver, MedalBronze, MedalIcon } from '../components/tickets/Tickets';
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { setIsFilterApplied, setUserFilters } from "../redux/userFilterSlice";
import * as general from '../libs/general';
import { fetchCustomerListPermissions, CUSTOMER_FILTER_GROUP_NAMES } from "../libs/customerCreatePermission";
import { customerDetailPath } from "../routes/customerRoutes";
import {
  CUSTOMER_MANAGEMENT_COLUMNS,
  CUSTOMER_MANAGEMENT_SCREEN_KEY,
  countVisibleScreenColumns,
  screenColumnClass,
} from "../libs/screenColumnPreference";
import { useScreenColumnPreferences } from "../libs/useScreenColumnPreferences";
import TableColumnSettingsButton from "../components/TableColumnSettingsButton";
import TableColumnSettingsModal from "../components/TableColumnSettingsModal";
import Paging from '../components/Paging';
import AdLayout from '../components/AdLayout';
import DataTable from '../components/ui/DataTable';

const DEFAULT_CUSTOMER_FILTER_GROUP = "Có mã";

/** Mặc định «Có mã»; nếu không được phân quyền thì nút lọc đầu tiên còn quyền. */
function pickDefaultCustomerFilterGroup(visibleButtons) {
  const names = visibleButtons.map((b) => b.groupName);
  if (names.length === 0) return "";
  if (names.includes(DEFAULT_CUSTOMER_FILTER_GROUP)) return DEFAULT_CUSTOMER_FILTER_GROUP;
  return names[0];
}

/** Số ngày (calendar) từ ngày đặt đơn cuối đến hôm nay; null nếu không tính được. */
function daysSinceLastOrder(lastOrderDate) {
  if (lastOrderDate == null || lastOrderDate === '') return null;
  const d = new Date(lastOrderDate);
  if (Number.isNaN(d.getTime())) return null;
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = new Date();
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((end - start) / 86400000);
}

function formatNum1(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const x = Number(n);
  return (Math.round(x * 10) / 10).toLocaleString('vi-VN');
}

function UserAvgOrderVolumeCell({ avgMonthlyOrderCount, currentMonthOrderCount, orderVolumeVsAvgPercent }) {
  if (orderVolumeVsAvgPercent == null && avgMonthlyOrderCount == null) {
    return <span className="text-muted">—</span>;
  }
  const titleParts = [];
  if (avgMonthlyOrderCount != null) {
    titleParts.push(`TB/tháng: ${formatNum1(avgMonthlyOrderCount)} đơn`);
  }
  if (currentMonthOrderCount != null) {
    titleParts.push(`Tháng này: ${currentMonthOrderCount} đơn`);
  }
  const title = titleParts.join(' | ');
  return (
    <span title={title || undefined}>
      {orderVolumeVsAvgPercent != null ? (
        <>
          <span className="fw-medium">{formatNum1(orderVolumeVsAvgPercent)}%</span>
          <span className="text-muted small"> TB</span>
        </>
      ) : (
        <span className="text-muted">—</span>
      )}
    </span>
  );
}

function UserLastOrderCell({ lastOrderDate }) {
  if (!lastOrderDate) {
    return <span className="text-muted">—</span>;
  }
  const n = daysSinceLastOrder(lastOrderDate);
  return (
    <span title={general.formatDateTime(lastOrderDate)}>
      {general.formatDate(lastOrderDate)}
      {n != null && (
        <span className="text-muted small d-block">({n} ngày)</span>
      )}
    </span>
  );
}

/** embedded: hiển thị trong tab của trang Quản lý khách hàng (không bọc AdLayout). */
const UserManagement = ({ embedded = false }) => {
  const currentUser = useSelector((state) => state.user.user) || {};
  const [pageData, setPageData] = useState({ content: [] });
  const [message, setMessage] = useState("");
  const hasRun = useRef(false);

  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [countUsersCustomerCodeList, setCountUsersCustomerCodeList] = useState([]);
  const [groupNameActive, setGroupNameActive] = useState("");
  const [canCreateCustomer, setCanCreateCustomer] = useState(currentUser?.isAdmin === true);
  const [customerFilterPerm, setCustomerFilterPerm] = useState({
    loaded: false,
    byGroup: {},
    hasGranular: false,
  });
  const { hiddenCols, setHiddenCols, loaded: columnSettingsLoaded } = useScreenColumnPreferences(
    CUSTOMER_MANAGEMENT_SCREEN_KEY,
    CUSTOMER_MANAGEMENT_COLUMNS
  );
  const [showColumnSettings, setShowColumnSettings] = useState(false);

  const handleEdit = (userId) => {
    navigate(customerDetailPath(userId));
  };

  const handleChangeItemsPerPage = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    searchUsers({ page: 0, size: newItemsPerPage });
  };

  const handleFilterUsersHaveCustomerCodeOrNot = async (name, id) => {
    setGroupNameActive(name);
    searchUsers({ coded: name, page: 0 });
  };

  const handlePageChange = (page) => {
    searchUsers({ page: page - 1 });
  };

  const handleSearchChange = (e) => {
    var searchValue = e.target.value;
    setSearchQuery(searchValue);
    searchUsers({ page: 0, keyword: searchValue });
  };

  const searchUsers = async (filter) => {
    if (window.calling) return;
    window.calling = true;
    setIsLoading(true);
    setMessage('');
    var params = general.getSession('search_users');

    var formData = general.getFormData('.form_filter');
    Object.assign(params, formData);

    if (!hasRun.current) {
      hasRun.current = true;
      var oldParams = general.getSession('search_users');
      Object.assign(params, oldParams);
      setSearchQuery(params.keyword);
    }

    if (filter) Object.assign(params, filter);
    params.isAdmin = false;
    general.setSession('search_users', params);
    window.initializeFilterValue(params);

    var filteredParams = Object.fromEntries(Object.entries(params).filter(([key, v]) => v !== undefined && v !== '' && key + v !== 'page0' && key + v !== 'size550' && key + v !== 'isDeletedfalse'));

    var response = {};
    try {
      response = await axiosInstance.get('/api/users/searchUsers', { params: filteredParams });
      setPageData(response.data);
      setUsers(response.data.page.content);
    } catch (error) {
      setPageData({ content: [] });
      setMessage(error.message);
      console.error(error);
    } finally {
      window.calling = false;
    }
    setIsLoading(false);
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const [permBundle, countsRes] = await Promise.all([
          fetchCustomerListPermissions(axiosInstance),
          axiosInstance.get("/api/users/countUsersCustomerCode"),
        ]);
        if (cancelled) return;

        setCanCreateCustomer(permBundle.canCreate);
        setCustomerFilterPerm({ loaded: true, ...permBundle.filter });
        const counts = countsRes?.data || [];
        setCountUsersCustomerCodeList(counts);

        const byGroup = permBundle.filter?.byGroup || {};
        const visibleButtons = counts.filter((obj) => byGroup[obj.groupName]);
        const sessionCoded = general.getSession("search_users")?.coded;
        let coded = "";
        if (visibleButtons.length > 0) {
          const sessionAllowed = sessionCoded && byGroup[sessionCoded];
          coded = sessionAllowed ? sessionCoded : pickDefaultCustomerFilterGroup(visibleButtons);
        }
        setGroupNameActive(coded);
        searchUsers({ coded, page: 0 });
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setCustomerFilterPerm({
            loaded: true,
            byGroup: Object.fromEntries(CUSTOMER_FILTER_GROUP_NAMES.map((g) => [g, true])),
            hasGranular: false,
          });
          searchUsers({ coded: DEFAULT_CUSTOMER_FILTER_GROUP, page: 0 });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const visibleFilterButtons = useMemo(() => {
    if (!customerFilterPerm.loaded) return [];
    return countUsersCustomerCodeList.filter((obj) => customerFilterPerm.byGroup[obj.groupName]);
  }, [countUsersCustomerCodeList, customerFilterPerm]);

  const colCls = (key) => screenColumnClass(hiddenCols, key);
  const visibleColCount = countVisibleScreenColumns(hiddenCols, CUSTOMER_MANAGEMENT_COLUMNS);

  const body = (
    <>
        <div className="table-responsive web-table customer-management-page customer-mgmt-screen">
          <div className="container-fluid">
            <div className="row bg-dark align-items-center">
              <div className="col-12 col-md-7 order-3 order-md-1 py-1 d-flex align-items-center">
                <div className="flex-start gap-2">
                  {canCreateCustomer && (
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={() => handleEdit(0)}
                      title="Khách mới"
                    >
                      <FaPlus /> Thêm mới
                    </button>
                  )}
                </div>
              </div>
              <div className="col-7 col-md-2 py-1 fw-bold text-white text-uppercase d-flex align-items-center justify-content-star order-1 order-md-2">
                Quản lý khách hàng
              </div>
              <div className="col-5 col-md-3 py-1 order-2 order-md-3 d-flex justify-content-end align-items-center">
                <form id="form_filter" className="form_filter mb-0 w-auto">
                  <div className="input-group search-block flex-nowrap">
                    <input
                      name="keyword"
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Tìm kiếm..."
                      value={searchQuery}
                      onChange={(e) => handleSearchChange(e)}
                    />
                    <button
                      onClick={(event) => searchUsers({ page: 0 })}
                      className="btn btn-light btn-sm"
                      type="button"
                      title="Tìm kiếm..."
                    >
                      <FaSearch />
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <div className="text-start mb-2 height-46">
              <div className="row">
                <div className="col-md-7 p-0 pt-1 express-filter">
                  {
                    visibleFilterButtons.map((obj, index) => (
                      <button key={index}
                        id={"btnFilter" + index}
                        className={`btn ${obj.groupName == groupNameActive ? 'btn btn-secondary' : 'btn-outline-secondary'} btn-sm me-2 my-1`}
                        onClick={() => { handleFilterUsersHaveCustomerCodeOrNot(obj.groupName, index) }}
                      >
                        {obj.groupName} <span className="ant-tag">{obj.count}</span>
                      </button>
                    ))
                  }
                </div>
                <div className="col-md-5 p-0 pt-1 ">
                  <span className="filter-des hidden-close-i2"></span>
                </div>
              </div>
            </div>
          </div>

          <section className="position-relative customer-mgmt-screen__table">
            <TableColumnSettingsButton
              onClick={() => setShowColumnSettings(true)}
              disabled={!columnSettingsLoaded}
            />
            <DataTable maxHeight="calc(100vh - 206px)" className="table-normal" data-loading={isLoading}>
              <table className="table table-striped border-normal table-bordered">
                <thead className="table-primary">
                  <tr>
                    <th className="text-center">#</th>
                    <th className={colCls("email")}>Email</th>
                    <th className={colCls("username")}>Tên đăng nhập</th>
                    <th className={`user-mgmt-name-col ${colCls("name")}`}>Tên</th>
                    <th className={colCls("levelName")}>Bảng giá</th>
                    <th className="d-none">Lines</th>
                    <th className={colCls("userContacts")}>Lines - Đối tác</th>
                    <th className={colCls("lastOrderDate")}>Đơn cuối</th>
                    <th className={colCls("avgOrder")} style={{ minWidth: "80px" }}>Trung bình</th>
                    <th className={`text-end ${colCls("customerMinWeightKg")}`} style={{ minWidth: "80px" }} title="Cân nặng tối thiểu - kg">Tối thiểu</th>
                    <th className={colCls("userRank")}>Thành tích</th>
                    <th className={colCls("customerCode")}>
                      <span>Mã KH</span>
                      <span className="filter-ib" data-form="form_filter" data-filter="codes" data-source-url={API_URL_All + '/api/users/searchUserCodes'} data-min="100px"><i className="filter-i"></i></span>
                    </th>
                    <th className={`text-center ${colCls("childUserCount")}`} style={{ minWidth: "72px" }}>Số user</th>
                    <th className={colCls("createdDate")}><span>Ngày</span></th>
                    <th className={`text-center ${colCls("actions")}`} aria-label="Thao tác"></th>
                  </tr>
                </thead>
                <tbody>
                  {pageData.page && pageData.page.content && pageData.page.content.map((user, index) => (
                    <tr key={user.id}>
                      <td className="text-center align-middle">{(pageData.page.number * pageData.page.size) + index + 1}</td>
                      <td className={`text-start ${colCls("email")}`}>
                        <button
                          type="button"
                          className="btn btn-link p-0 text-start text-decoration-none"
                          style={{ color: "#485766" }}
                          onClick={() => handleEdit(user.id)}
                          title="Xem chi tiết khách hàng"
                        >
                          {user.email || "—"}
                        </button>
                      </td>
                      <td className={`text-start ${colCls("username")}`}>{user.username || "—"}</td>
                      <td className={`text-start user-mgmt-name-col ${colCls("name")}`}>
                        <button
                          type="button"
                          className="btn btn-link p-0 text-start text-decoration-none"
                          style={{ color: "#485766" }}
                          onClick={() => handleEdit(user.id)}
                          title={user.name || "Xem chi tiết khách hàng"}
                        >
                          {user.name || "—"}
                        </button>
                      </td>
                      <td className={`text-start ${colCls("levelName")}`}>
                        {user.levelId ? (
                          <button
                            type="button"
                            className="btn btn-link p-0 text-start text-decoration-none"
                            style={{ color: "#485766" }}
                            onClick={() => navigate(`/level/${user.levelId}`)}
                            title="Xem bảng giá khách hàng"
                          >
                            {user.levelName || "—"}
                          </button>
                        ) : (
                          user.levelName
                        )}
                      </td>
                      <td className="text-start d-none" dangerouslySetInnerHTML={{ __html: general.formatSelect3(user.lines) }}></td>
                      <td className={`text-start ${colCls("userContacts")}`}>

                        {user.userContacts && user.userContacts.map((item2, index2) => (
                          <span>
                            {item2.contactCode && (
                              <span className="block-02 bg-blue" key={index2}>{item2.lineName} - {item2.contactCode}</span>
                            )}
                            {!item2.contactCode && (
                              <span className="select3-item" key={index2}>{item2.lineName}</span>
                            )}
                            
                          </span>
                        ))}
                      </td>
                      <td className={`text-start ${colCls("lastOrderDate")}`}>
                        <UserLastOrderCell lastOrderDate={user.lastOrderDate} />
                      </td>
                      <td className={`text-start ${colCls("avgOrder")}`} style={{ minWidth: "80px" }}>
                        <UserAvgOrderVolumeCell
                          avgMonthlyOrderCount={user.avgMonthlyOrderCount}
                          currentMonthOrderCount={user.currentMonthOrderCount}
                          orderVolumeVsAvgPercent={user.orderVolumeVsAvgPercent}
                        />
                      </td>
                      <td className={`text-end align-middle ${colCls("customerMinWeightKg")}`} style={{ minWidth: "80px" }}>
                        {user.customerMinWeightKg != null && Number(user.customerMinWeightKg) > 0 ? (
                          <span title="Cân tối thiểu khi tính phí KH (theo user)">{formatNum1(user.customerMinWeightKg)}</span>
                        ) : (
                          <span className="text-muted" title="Đang dùng cấu hình hệ thống">—</span>
                        )}
                      </td>
                      <td className={`text-start ${colCls("userRank")}`}><MedalIcon rank={user.userRank} /></td>
                      <td className={`text-start ${colCls("customerCode")}`}>{user.customerCode}</td>
                      <td className={`text-center align-middle ${colCls("childUserCount")}`}>
                        {user.childUserCount != null ? user.childUserCount : 0}
                      </td>
                      <td className={colCls("createdDate")}>
                        <span title={user.createdDate ? general.formatDateTime2(user.createdDate) : undefined}>
                          {general.formatDateDayMonth(user.createdDate)}
                        </span>
                      </td>
                      <td className={`text-center align-middle ${colCls("actions")}`}>
                        <FaPen
                          className="text-primary"
                          role="button"
                          tabIndex={0}
                          onClick={() => handleEdit(user.id)}
                          onKeyDown={(e) => e.key === "Enter" && handleEdit(user.id)}
                          size={15}
                          title="Cập nhật"
                        />
                      </td>
                    </tr>
                  ))}

                  {message === '' && pageData.page && pageData.page.content.length === 0 && (
                    <tr>
                      <td colSpan={visibleColCount} className="text-center">Không tìm thấy dữ liệu</td>
                    </tr>
                  )}
                  {message !== '' && (
                    <tr>
                      <td colSpan={visibleColCount} className="text-center">{message}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </DataTable>
          </section>
          <TableColumnSettingsModal
            show={showColumnSettings}
            onClose={() => setShowColumnSettings(false)}
            screenKey={CUSTOMER_MANAGEMENT_SCREEN_KEY}
            columns={CUSTOMER_MANAGEMENT_COLUMNS}
            hiddenColumns={hiddenCols}
            onSaved={(cols) => setHiddenCols(cols)}
          />
          <div className="customer-mgmt-screen__footer d-flex justify-content-end align-items-center">
            <Paging
              pageData={pageData}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleChangeItemsPerPage}
            />
          </div>
        </div>
    </>
  );

  if (embedded) return body;
  return (
    <div>
      <AdLayout>{body}</AdLayout>
    </div>
  );
}
export default UserManagement;